/**
 * Planapus-style design language for goodshowhq.
 *
 * Light, neutral Todoist-inspired palette. The MUI theme itself stays almost
 * empty (defaults + no-uppercase buttons/tabs); visual identity is expressed
 * via small `sx`-style factories exported here so pages stay readable.
 */
import {createTheme} from '@mui/material/styles';
import type {SxProps, Theme} from '@mui/material/styles';
import {ScoreDirection} from '../types/generated/graphql';

export const pcolors = {
  bg: '#F9FAFB',
  card: '#FFFFFF',
  border: '#E5E7EB',
  borderLight: '#F3F4F6',
  textPrimary: '#111827',
  textSecondary: '#6B7280',
  textMuted: '#9CA3AF',
  textDisabled: '#D1D5DB',
  headerBg: '#F9FAFB',
  rowHover: '#F9FAFB',
  rowSelected: '#F0F5FF',

  blue: {50: '#EFF6FF', 500: '#3B82F6', 600: '#2563EB', 700: '#1D4ED8', 900: '#1E40AF'},
  green: {50: '#ECFDF5', 100: '#D1FAE5', 500: '#10B981', 600: '#059669', 700: '#065F46'},
  amber: {50: '#FFFBEB', 100: '#FEF3C7', 500: '#F59E0B', 700: '#92400E'},
  orange: {100: '#FED7AA', 700: '#9A3412'},
  red: {50: '#FEF2F2', 100: '#FEE2E2', 500: '#EF4444', 600: '#DC2626', 700: '#991B1B'},
  purple: {500: '#9333EA'}
};

/**
 * Score → {bg, color} per planapus's thresholds.
 *
 * `direction` is REQUIRED (no default) so every caller is forced to
 * think about it — detection agents (LOWER_IS_BETTER) render a clean
 * "2/10" as green, not red. For aggregates and eval reports that are
 * always higher-better, pass `ScoreDirection.HigherIsBetter` explicitly.
 *
 * Accepts 0..1 (eval overallScore) or 0..100 (percentage). Raw 0..10
 * values MUST be pre-normalized by the caller (divide by 10); the
 * `score <= 1` heuristic cannot disambiguate 0..10 from 0..100.
 */
export function scoreColors(
  score: number | null | undefined,
  direction: ScoreDirection,
): {bg: string; color: string} {
  if (score == null) return {bg: pcolors.borderLight, color: pcolors.textSecondary};
  let pct = score <= 1 ? score * 100 : score;
  if (direction === ScoreDirection.LowerIsBetter) {
    pct = 100 - pct;
  }
  if (pct >= 90) return {bg: pcolors.green[100], color: pcolors.green[700]};
  if (pct >= 70) return {bg: pcolors.amber[100], color: pcolors.amber[700]};
  if (pct >= 50) return {bg: pcolors.orange[100], color: pcolors.orange[700]};
  return {bg: pcolors.red[100], color: pcolors.red[700]};
}

/**
 * Chip that pops from a numeric score. `direction` is REQUIRED —
 * see `scoreColors` for why.
 */
export function scoreBadgeSx(
  score: number | null | undefined,
  direction: ScoreDirection,
): SxProps<Theme> {
  const {bg, color} = scoreColors(score, direction);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 1.5,
    py: 0.5,
    borderRadius: 1.5,
    bgcolor: bg,
    color,
    fontSize: 13,
    fontWeight: 700,
    minWidth: 48,
    cursor: 'pointer',
    transition: 'transform 0.1s, box-shadow 0.1s',
    '&:hover': {transform: 'scale(1.08)', boxShadow: `0 2px 8px ${bg}`}
  } as SxProps<Theme>;
}

// Shared `sx` factories -------------------------------------------------------

export const cardSx: SxProps<Theme> = {
  bgcolor: pcolors.card,
  borderRadius: '12px',
  border: `1px solid ${pcolors.border}`
};

export const pageSx: SxProps<Theme> = {p: 3, maxWidth: 1200, mx: 'auto'};

export const pageTitleSx: SxProps<Theme> = {
  fontSize: 22,
  fontWeight: 700,
  color: pcolors.textPrimary
};

export const sectionLabelSx: SxProps<Theme> = {
  fontSize: 12,
  fontWeight: 600,
  color: pcolors.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: 0.4
};

export const subtleLabelSx: SxProps<Theme> = {fontSize: 10, color: pcolors.textMuted};

export const tableHeadRowSx: SxProps<Theme> = {bgcolor: pcolors.headerBg};
export const tableHeadCellSx: SxProps<Theme> = {
  fontSize: 12,
  fontWeight: 600,
  color: pcolors.textSecondary
};
export const tableBodyCellSx: SxProps<Theme> = {fontSize: 13};

export const listRowSx = (selected = false): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  py: 1,
  cursor: 'pointer',
  bgcolor: selected ? pcolors.rowSelected : 'transparent',
  borderBottom: `1px solid ${pcolors.borderLight}`,
  '&:last-of-type': {borderBottom: 'none'},
  '&:hover': {bgcolor: selected ? pcolors.rowSelected : pcolors.rowHover}
});

/**
 * Minimal MUI theme. Planapus leaves almost everything at defaults and
 * overrides visuals via sx; we match the two things worth globalizing:
 * no-uppercase buttons/tabs.
 */
export const planapusTheme = createTheme({
  palette: {
    mode: 'light',
    background: {default: pcolors.bg, paper: pcolors.card},
    divider: pcolors.border,
    text: {primary: pcolors.textPrimary, secondary: pcolors.textSecondary}
  },
  shape: {borderRadius: 8},
  components: {
    MuiButton: {
      defaultProps: {disableElevation: true},
      styleOverrides: {root: {textTransform: 'none', fontSize: 13, borderRadius: 8}}
    },
    MuiTab: {
      styleOverrides: {root: {textTransform: 'none', fontSize: 13, minHeight: 40}}
    },
    MuiPaper: {styleOverrides: {root: {backgroundImage: 'none'}}},
    MuiAppBar: {
      defaultProps: {elevation: 0},
      styleOverrides: {
        root: {
          backgroundColor: pcolors.card,
          color: pcolors.textPrimary,
          borderBottom: `1px solid ${pcolors.border}`
        }
      }
    }
  },
  breakpoints: {values: {xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920}}
});

/**
 * GoodShowHQ marketing theme — used by the public landing page only.
 * Distinct from `planapusTheme` (which serves the parent-facing app
 * and admin tools): the landing page lives behind a local
 * `<ThemeProvider>` wrap so its bolder typography and indigo
 * primary don't bleed into the rest of the app.
 *
 * Brand palette anchored to the logo gradient (logo-mark.svg):
 * indigo `#6366F1` → `#3448F0`. Background stays pure white so
 * the slate-50 section bands read as the only off-white surface.
 */
export const goodShowHQTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#3448F0',
      light: '#6366F1',
      dark: '#2839C9'
    },
    secondary: {main: '#FFD166'},
    background: {default: '#FFFFFF', paper: '#FFFFFF'},
    text: {primary: '#0E1326', secondary: '#475569'}
  },
  shape: {borderRadius: 16},
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {fontWeight: 800, letterSpacing: '-0.02em'},
    h2: {fontWeight: 800, letterSpacing: '-0.02em'},
    h3: {fontWeight: 800, letterSpacing: '-0.01em'},
    body1: {fontWeight: 400},
    body2: {fontWeight: 400}
  },
  components: {
    MuiButton: {
      defaultProps: {disableElevation: true},
      styleOverrides: {
        root: {textTransform: 'none', borderRadius: 999, fontWeight: 700}
      }
    }
  }
});
